import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createStage, fetchStages, updateStage } from '@/api/client'
import type { Stage, StageCategory } from '@/api/types'
import { ApiError } from '@/api/client'
import { percentToProbability, probabilityToPercent } from '@/lib/format-probability'
import { StageBadge } from '@/components/StageBadge'

import { Layers } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const CATEGORIES: StageCategory[] = ['committed', 'open_pipeline', 'lost']

export function StagesAdminPage() {
  const qc = useQueryClient()
  const stagesQuery = useQuery({
    queryKey: ['config', 'stages', 'admin'],
    queryFn: () => fetchStages({ includeRetired: true }),
  })
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    code: '',
    name: '',
    probability: '50',
    category: 'open_pipeline' as StageCategory,
    sort_order: 10,
  })

  const createMut = useMutation({
    mutationFn: createStage,
    onSuccess: async () => {
      setError(null)
      setDraft({
        code: '',
        name: '',
        probability: '50',
        category: 'open_pipeline',
        sort_order: 10,
      })
      await qc.invalidateQueries({ queryKey: ['config', 'stages'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create stage')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateStage>[1] }) =>
      updateStage(id, body),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'stages'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update stage')
    },
  })

  const stages = stagesQuery.data ?? []

  return (
    <AdminSection
      title="Stages"
      tone="azure"
      icon={Layers}
      lede="Maintain stage names, probabilities, and categories. Retire stages in use — do not delete."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th className="table__num">Probability</th>
              <th>Category</th>
              <th className="table__num">Order</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <StageRow
                key={stage.id}
                stage={stage}
                busy={patchMut.isPending}
                onSave={(body) => patchMut.mutate({ id: stage.id, body })}
              />
            ))}
          </tbody>
        </table>
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          createMut.mutate({
            code: draft.code.trim(),
            name: draft.name.trim(),
            probability: percentToProbability(draft.probability),
            category: draft.category,
            sort_order: Number(draft.sort_order),
          })
        }}
      >
        <h3 className="admin-form__title">Add stage</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="field__input"
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              required
            />
          </label>
          <label className="field field--numeric">
            <span className="field__label">Probability (%)</span>
            <div className="field__affix">
              <input
                className="field__input field__input--numeric"
                type="number"
                min={0}
                max={100}
                step={1}
                value={draft.probability}
                onChange={(e) => setDraft((d) => ({ ...d, probability: e.target.value }))}
                required
              />
              <span className="field__affix-label">%</span>
            </div>
          </label>
          <label className="field">
            <span className="field__label">Category</span>
            <select
              className="field__input"
              value={draft.category}
              onChange={(e) =>
                setDraft((d) => ({ ...d, category: e.target.value as StageCategory }))
              }
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Sort</span>
            <input
              className="field__input"
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) }))}
            />
          </label>
          <button className="button button--primary" type="submit" disabled={createMut.isPending}>
            Add
          </button>
        </div>
      </form>
    </AdminSection>
  )
}

function StageRow({
  stage,
  busy,
  onSave,
}: {
  stage: Stage
  busy: boolean
  onSave: (body: {
    name?: string
    probability?: number
    category?: StageCategory
    sort_order?: number
    retired?: boolean
  }) => void
}) {
  const [name, setName] = useState(stage.name)
  const [probabilityPct, setProbabilityPct] = useState(
    String(probabilityToPercent(stage.probability) ?? 0),
  )
  const [category, setCategory] = useState<StageCategory>(stage.category)
  const [sortOrder, setSortOrder] = useState(stage.sort_order ?? 0)

  return (
    <tr className={stage.retired ? 'table__row--muted' : undefined}>
      <td>
        <input
          className="field__input field__input--compact"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td>
        <code>{stage.code}</code>
      </td>
      <td className="table__num">
        <div className="field__affix">
          <input
            className="field__input field__input--compact field__input--numeric"
            type="number"
            min={0}
            max={100}
            step={1}
            value={probabilityPct}
            onChange={(e) => setProbabilityPct(e.target.value)}
            aria-label={`Probability for ${stage.name}`}
          />
          <span className="field__affix-label">%</span>
        </div>
      </td>
      <td>
        <div className="admin-inline-field">
          <StageBadge name={category.replace(/_/g, ' ')} category={category} />
          <select
            className="field__input field__input--compact"
            value={category}
            onChange={(e) => setCategory(e.target.value as StageCategory)}
            aria-label="Stage category"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td className="table__num">
        <input
          className="field__input field__input--compact field__input--numeric"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
        />
      </td>
      <td className="table__actions">
        <button
          type="button"
          className="button button--ghost"
          disabled={busy}
          onClick={() =>
            onSave({
              name,
              probability: percentToProbability(probabilityPct),
              category,
              sort_order: sortOrder,
            })
          }
        >
          Save
        </button>
        <button
          type="button"
          className="button button--ghost"
          disabled={busy}
          onClick={() => onSave({ retired: !stage.retired })}
        >
          {stage.retired ? 'Restore' : 'Retire'}
        </button>
      </td>
    </tr>
  )
}
